import React, { useEffect, useState } from "react";
import { GripVertical, Plus, Trash2, ChevronUp, ChevronDown, Save, RefreshCw, Lock } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { useToast } from "../../hooks/use-toast";
import { API, authConfig, axios } from "../../lib/auth";

// name & email are essential contact info — kept always visible.
const LOCKED = ["name", "email"];

const FormBuilder = ({ onAuthError }) => {
  const { toast } = useToast();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleErr = (err) => { if (err?.response?.status === 401) onAuthError?.(); };

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/form-config`, authConfig());
      setFields(res.data);
    } catch (err) { handleErr(err); toast({ title: "Failed to load form", variant: "destructive" }); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateField = (i, patch) => setFields((prev) => prev.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    setFields((prev) => {
      const copy = [...prev];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  };

  const removeField = (i) => setFields((prev) => prev.filter((_, idx) => idx !== i));

  const addField = (type) => {
    const key = `custom_${Date.now()}`;
    setFields((prev) => [
      ...prev,
      {
        key,
        label: type === "select" ? "New dropdown question" : "New question",
        type,
        placeholder: "",
        help_text: "",
        options: type === "select" ? ["Option 1", "Option 2"] : [],
        width: "full",
        visible: true,
        required: false,
        system: false,
        order: prev.length + 1,
      },
    ]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/form-config`, { fields }, authConfig());
      setFields(res.data);
      toast({ title: "Form saved", description: "Your contact form has been updated." });
    } catch (err) { handleErr(err); toast({ title: "Couldn't save form", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-16 text-center text-slate-400">Loading form...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900">Contact form builder</h2>
          <p className="text-sm text-slate-500">Show or hide questions, mark them required, and reorder them. Changes go live on your contact page.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchConfig} variant="outline" className="font-semibold"><RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />Reset</Button>
          <Button onClick={save} disabled={saving} className="brand-gradient text-white font-semibold"><Save className="mr-2 h-4 w-4" />{saving ? "Saving..." : "Save changes"}</Button>
        </div>
      </div>

      <div className="space-y-4">
        {fields.map((f, i) => {
          const locked = LOCKED.includes(f.key);
          return (
            <div key={f.key} className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5">
              <div className="flex items-start gap-4">
                <div className="flex flex-col items-center pt-1 text-slate-300">
                  <button type="button" onClick={() => move(i, -1)} className="hover:text-blue-600" title="Move up"><ChevronUp className="h-4 w-4" /></button>
                  <GripVertical className="h-4 w-4" />
                  <button type="button" onClick={() => move(i, 1)} className="hover:text-blue-600" title="Move down"><ChevronDown className="h-4 w-4" /></button>
                </div>

                <div className="flex-1 space-y-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex-1 min-w-[220px] space-y-1">
                      <Label className="text-xs text-slate-500">Question label</Label>
                      <Input value={f.label} onChange={(e) => updateField(i, { label: e.target.value })} />
                    </div>
                    <Badge variant="secondary" className="font-medium capitalize">{f.type}</Badge>
                    {f.system && <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50 font-medium">Core</Badge>}
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Help text (optional hint shown under the question)</Label>
                    <Input value={f.help_text || ""} onChange={(e) => updateField(i, { help_text: e.target.value })} placeholder="e.g. A rough range is totally fine — we'll refine it together." />
                  </div>

                  {f.type === "select" && (
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Dropdown options (one per line)</Label>
                      <Textarea
                        rows={Math.max(2, (f.options || []).length)}
                        value={(f.options || []).join("\n")}
                        onChange={(e) => updateField(i, { options: e.target.value.split("\n").map((o) => o.trim()).filter(Boolean) })}
                      />
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
                    <div className="flex items-center gap-2">
                      <Switch checked={f.visible} disabled={locked} onCheckedChange={(v) => updateField(i, { visible: v })} />
                      <span className="text-sm font-medium text-slate-700 flex items-center gap-1">
                        Visible {locked && <Lock className="h-3 w-3 text-slate-400" />}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={f.required} onCheckedChange={(v) => updateField(i, { required: v })} />
                      <span className="text-sm font-medium text-slate-700">Required</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500">Width</span>
                      <Select value={f.width} onValueChange={(v) => updateField(i, { width: v })}>
                        <SelectTrigger className="h-8 w-[110px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="half">Half</SelectItem>
                          <SelectItem value="full">Full</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {!f.system && (
                      <Button variant="ghost" size="sm" onClick={() => removeField(i)} className="ml-auto text-slate-400 hover:text-red-600">
                        <Trash2 className="h-4 w-4 mr-1" />Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button variant="outline" onClick={() => addField("select")} className="font-semibold"><Plus className="mr-2 h-4 w-4" />Add dropdown question</Button>
        <Button variant="outline" onClick={() => addField("text")} className="font-semibold"><Plus className="mr-2 h-4 w-4" />Add text question</Button>
      </div>
    </div>
  );
};

export default FormBuilder;
